import React, { useState } from 'react'

import SaveService from 'services/save.service'
import ToastService from 'services/toast.service'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { useCanEditDeck } from 'redux/deck/selectors'

import Modal from 'components/elements/Modal'
import AddToOtherDeck from 'components/card/cardForm/addToOtherDeck'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import MultiSelectEditingOverlay from 'components/deckPage/modals/MultiSelectEditingOverlay'
import AddToCollectionMultiSelect from 'components/deckPage/misc/AddToCollectionMultiSelect'
import DownloadImagesModal from 'components/deckPage/modals/DownloadImagesModal'
import Icon from 'components/elements/Icon'
import FloatingMenuWrapper from 'components/misc/FloatingMenu/Wrapper'
import CardComparisonOverlay from 'components/deckPage/modals/cardComparisonOverlay'

import { buyFromDropdown } from 'components/formElements/BuyCardsLink'
import { getMultiSelectedUpdates } from 'utils/CategoryChangeHelpers'
import { getDeckPriceAndInfo } from 'utils/calculateDeckPrice'
import { getPriceStringFromFloat } from 'utils/Price'
import { copyCardNamesToClipboard } from 'utils/cardsToClipboard'

import styles from './multiSelectControls.module.scss'

const MultiSelectControls = () => {
  const canEditDeck = useCanEditDeck()
  const multiSelectedIds = useAppSelector(state => state.active.multiSelectedIds)
  const priceSource = useAppSelector(state => state.active.priceSource)
  const categories = useAppSelector(state => state.deck.categories)
  const cardMap = useAppSelector(state => state.deck.cardMap)

  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const [addToOtherDeckModal, setAddToOtherDeckModal] = useState(false)
  const [addToCollectionModal, setAddToCollectionModal] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [downloadImagesModal, setDownloadImagesModal] = useState(false)

  const selectedCards = Object.keys(multiSelectedIds).map(id => cardMap[id])

  const totalSelected = selectedCards.reduce((acc, card) => acc + (card?.qty || 0), 0)
  const { estDeckCost } = getDeckPriceAndInfo(selectedCards, categories, priceSource)
  const estPrice = getPriceStringFromFloat(estDeckCost, priceSource[0])

  const handleRemoveAll = () => {
    const removedIds = Object.keys(multiSelectedIds)

    SaveService.save(selectedCards, removedIds)
      .then(() => setActiveState({ multiSelectedIds: {} }))
      .catch(() => ToastService.create('Failed to remove cards', 'Multi-edit', 'error'))
  }

  const handleMoveToCategory = (category: string | null) => {
    const updatedCards = getMultiSelectedUpdates(multiSelectedIds, '', category)

    SaveService.save(updatedCards).catch(() => ToastService.create('Failed to move cards', 'Multi-edit', 'error'))
  }

  const handleOnAddedCardsToOtherDeck = () => {
    setAddToOtherDeckModal(false)
    setActiveState({ multiSelectedIds: {} })
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.info}>
          <label>{totalSelected} Card(s) Selected</label>
          <label className={styles.selectedPrice}>Selected price {estPrice}</label>
        </div>
        <div className={styles.controls}>
          {canEditDeck && (
            <button className={styles.button} onClick={() => setEditModalOpen(true)}>
              <Icon style={{ marginLeft: '.3rem' }} name="edit" /> <span>Edit</span>
            </button>
          )}

          <button className={styles.button} onClick={() => setActiveState({ multiSelectedIds: {} })}>
            <Icon name="close" /> <span>Unselect</span>
          </button>

          <ArchidektDropdown
            triggerClassName={styles.dropdownTrigger}
            menuClassName={styles.quickOptions}
            defaultTriggerClassName={styles.defaultTrigger}
            direction="up"
            options={[
              { icon: 'address book', label: 'Add to Collection', onClick: () => setAddToCollectionModal(true) },
              { icon: 'add', label: 'Add to other Deck', onClick: () => setAddToOtherDeckModal(true) },
              buyFromDropdown(Object.keys(multiSelectedIds), priceSource[0], 'multi-select'),
              { type: 'spacer', hidden: !canEditDeck },
              {
                icon: 'eye',
                label: 'Compare selected cards',
                onClick: () => setActiveState({ compareCardsOverlay: true }),
              },
              { type: 'spacer' },
              { icon: 'trash', label: 'Delete all', onClick: handleRemoveAll, hidden: !canEditDeck },
              { type: 'spacer', hidden: !canEditDeck },
              { icon: 'download', label: 'Download card images', onClick: () => setDownloadImagesModal(true) },
              {
                type: 'extras',
                label: 'Copy to clipboard',
                icon: 'copy',
                id: 'copy-to-clipboard-options',
                options: [
                  {
                    label: 'Card names only',
                    onClick: () => copyCardNamesToClipboard(selectedCards),
                  },
                  {
                    label: 'Card name with quantities',
                    onClick: () => copyCardNamesToClipboard(selectedCards, true),
                  },
                ],
              },
              { type: 'spacer', hidden: !canEditDeck },
              {
                icon: 'arrow right',
                label: 'Move to category...',
                type: 'extras',
                id: 'move-to-category-options',
                hidden: !canEditDeck,
                className: styles.moveCategory,
                options: [
                  {
                    label: 'Auto categories',
                    onClick: () => handleMoveToCategory(null),
                  },
                  {
                    label: 'Maybeboard',
                    onClick: () => handleMoveToCategory('Maybeboard'),
                  },
                  {
                    label: 'Sideboard',
                    onClick: () => handleMoveToCategory('Sideboard'),
                  },
                  { type: 'spacer' },
                  ...Object.values(categories)
                    .map(category => ({
                      label: category.name,
                      onClick: () => handleMoveToCategory(category.name),
                      hidden: ['Maybeboard', 'Sideboard'].includes(category.name),
                    }))
                    .sort((a, b) => (a.label > b.label ? 1 : -1)),
                ],
              },
              {
                icon: 'money',
                label: 'Optimize printings',
                onClick: () => setActiveState({ optimizeCardOverlay: true }),
                hidden: !canEditDeck,
              },
            ]}
          />
        </div>
      </div>

      <MultiSelectEditingOverlay open={editModalOpen} onClose={() => setEditModalOpen(false)} key="overlay" />

      <AddToOtherDeck
        parentControlled
        cards={selectedCards}
        open={addToOtherDeckModal}
        onClose={() => setAddToOtherDeckModal(false)}
        onAdded={handleOnAddedCardsToOtherDeck}
        key="add-to-other-deck"
      />

      {addToCollectionModal && (
        <Modal
          open
          onClose={() => setAddToCollectionModal(false)}
          closeButton
          modalClassName={styles.modal}
          key="add-to-collection">
          <h3>Add to collection</h3>
          <div>
            <AddToCollectionMultiSelect
              onAdded={() => {
                setAddToCollectionModal(false)
                setActiveState({ multiSelectedIds: {} })
              }}
            />
          </div>
        </Modal>
      )}
      {downloadImagesModal && (
        <Modal
          open
          onClose={() => setDownloadImagesModal(false)}
          closeButton
          modalClassName={styles.modal}
          key="download-cards-modal">
          <h3>Download card images</h3>
          <div>
            <DownloadImagesModal />
          </div>
        </Modal>
      )}
    </>
  )
}

export default MultiSelectControls
